import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Building2, AlertTriangle, CheckCircle2, Link2, XCircle, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { formatCurrency, isZeroMoney } from '../../lib/utils';
import { subcontractReconciliationApi as recApi } from '../../lib/api';

const STATUS_META = {
  NON_AFFIDATO: { label: 'Non affidato', cls: 'bg-slate-100 text-slate-600' },
  OFFERTA_DA_REVISIONARE: { label: 'Offerta da revisionare', cls: 'bg-sky-100 text-sky-700' },
  DA_RICONCILIARE: { label: 'Da riconciliare', cls: 'bg-amber-100 text-amber-700' },
  PARZIALMENTE_RICONCILIATO: { label: 'Parzialmente riconciliato', cls: 'bg-orange-100 text-orange-700' },
  ALLINEATO: { label: 'Allineato', cls: 'bg-emerald-100 text-emerald-700' },
  BLOCCATO_DA_SAL: { label: 'Bloccato da SAL', cls: 'bg-red-100 text-red-700' },
  BLOCCATO_DA_SCOPE: { label: 'Bloccato da scope', cls: 'bg-red-100 text-red-700' },
};

const fmtQty = (v, unit) => `${(Number(v) || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}${unit ? ' ' + unit : ''}`;
const errCode = (e) => e?.response?.data?.detail?.code;
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return fb || 'Errore';
};

const identityLabel = (t) => t.measure_line_label ? `${t.item_code} · ${t.measure_line_label}` : (t.item_code || t.sal_item_id);

export const SubcontractImpactSection = ({ projectId, variationId, canWrite = false }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyLink, setBusyLink] = useState(null);
  const [preview, setPreview] = useState(null); // { target, candidate, mag, data, error, loading }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await recApi.getImpact(projectId, variationId);
      setImpact(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Errore caricamento impatto subappalti'));
      setImpact(null);
    } finally { setLoading(false); }
  }, [projectId, variationId]);

  useEffect(() => { load(); }, [load]);

  const runPreview = async (target, candidate, mag) => {
    const sign = target.direction === 'DECREASE' ? -1 : 1;
    setPreview((p) => ({ ...(p || {}), target, candidate, mag, loading: true, error: null }));
    try {
      const r = await recApi.preview(projectId, variationId, {
        sal_item_id: target.sal_item_id,
        subcontract_item_id: candidate.subcontract_item_id,
        quantity_delta_signed: sign * Math.abs(Number(mag) || 0),
      });
      setPreview({ target, candidate, mag, data: r.data, dataMag: Math.abs(Number(mag) || 0), loading: false, error: null });
    } catch (e) {
      setPreview({ target, candidate, mag, data: null, loading: false,
        error: { code: errCode(e), message: errMsg(e, 'Anteprima non disponibile'),
                 max_reducible: e?.response?.data?.detail?.max_reducible } });
    }
  };

  const openPreview = (target, candidate) => {
    const mag = Math.abs(target.remaining_allocatable || target.client_delta_abs || 0);
    runPreview(target, candidate, mag);
  };

  const confirmDraft = async () => {
    if (!preview?.data) return;
    const { target, candidate, mag } = preview;
    const sign = target.direction === 'DECREASE' ? -1 : 1;
    setPreview((p) => ({ ...p, submitting: true }));
    try {
      await recApi.create(projectId, variationId, {
        sal_item_id: target.sal_item_id,
        subcontract_item_id: candidate.subcontract_item_id,
        quantity_delta_signed: sign * Math.abs(Number(mag) || 0),
        measure_line_id: target.measure_line_id || null,
      });
      toast.success('Modifica sub creata in bozza');
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Errore creazione bozza'));
      setPreview((p) => ({ ...p, submitting: false }));
    }
  };

  const applyLink = async (linkId) => {
    setBusyLink(linkId);
    try { await recApi.apply(projectId, linkId); toast.success('Modifica sub resa effettiva'); await load(); }
    catch (e) { toast.error(errMsg(e, 'Errore applicazione')); }
    finally { setBusyLink(null); }
  };
  const cancelLink = async (linkId) => {
    setBusyLink(linkId);
    try { await recApi.cancel(projectId, linkId); toast.success('Modifica sub annullata'); await load(); }
    catch (e) { toast.error(errMsg(e, 'Errore annullamento')); }
    finally { setBusyLink(null); }
  };

  if (loading) {
    return (
      <div className="mt-4 rounded-lg border border-slate-100 p-4 flex items-center gap-2 text-sm text-slate-500" data-testid="cv-subimpact-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento impatto subappalti…
      </div>
    );
  }
  const targets = impact?.targets || [];
  if (!targets.length) return null;

  return (
    <div className="mt-4" data-testid="cv-subimpact-section">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-[#FF7819]" />
        <h4 className="text-sm font-semibold text-[#171717]">Impatto sui subappalti</h4>
      </div>
      <div className="space-y-3">
        {targets.map((t) => {
          const meta = STATUS_META[t.status] || { label: t.status, cls: 'bg-slate-100 text-slate-600' };
          const pendingLinks = (t.links || []).filter((l) => l.reconciliation_state === 'pending');
          return (
            <div key={`${t.sal_item_id}-${t.measure_line_id || ''}`} className="rounded-xl border border-[#ECECEC] bg-white p-3.5" data-testid={`cv-subimpact-target-${t.sal_item_id}`}>
              {/* Header: identità + stato */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-[#171717] truncate" data-testid="cv-subimpact-identity">{identityLabel(t)}</p>
                  {t.item_description && <p className="text-xs text-[#8a8a8a] truncate">{t.item_description}</p>}
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded ${meta.cls}`} data-testid={`cv-subimpact-status-${t.sal_item_id}`}>{meta.label}</span>
              </div>

              {/* Delta cliente + valore */}
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-[#737373]">Delta cliente <b className="text-[#171717]">{t.direction === 'DECREASE' ? '−' : '+'}{fmtQty(t.client_delta_abs, t.unit)}</b></span>
                <span className="text-[#737373]">Valore cliente <b className={t.client_delta_amount < 0 ? 'text-red-600' : 'text-emerald-700'}>{isZeroMoney(t.client_delta_amount) ? '' : (t.client_delta_amount < 0 ? '−' : '+')}{formatCurrency(Math.abs(t.client_delta_amount))}</b></span>
              </div>

              {/* Riepilogo riconciliazione */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs" data-testid="cv-subimpact-summary">
                <div><p className="text-[#9a9a9a]">Delta cliente</p><p className="font-medium">{fmtQty(t.client_delta_abs, t.unit)}</p></div>
                <div><p className="text-[#9a9a9a]">In corso</p><p className="font-medium text-orange-600">{fmtQty(t.pending_allocated, t.unit)}</p></div>
                <div><p className="text-[#9a9a9a]">Riconciliato</p><p className="font-medium text-emerald-700">{fmtQty(t.effective_reconciled, t.unit)}</p></div>
                <div><p className="text-[#9a9a9a]">Residuo</p><p className="font-medium">{fmtQty(t.remaining_allocatable, t.unit)}</p></div>
              </div>

              {/* Stati puliti */}
              {t.status === 'NON_AFFIDATO' && (
                <p className="mt-3 text-xs text-[#8a8a8a] bg-slate-50 rounded-md px-3 py-2" data-testid="cv-subimpact-non-affidato">
                  Questa lavorazione non risulta affidata a un subappaltatore.
                </p>
              )}
              {t.status === 'OFFERTA_DA_REVISIONARE' && (
                <p className="mt-3 text-xs text-sky-700 bg-sky-50 rounded-md px-3 py-2" data-testid="cv-subimpact-offerta">
                  Questa lavorazione è presente in un'offerta non ancora contrattualizzata.
                </p>
              )}

              {/* Candidati */}
              {(t.candidates || []).map((c) => {
                const clink = (t.links || []).filter((l) => l.subcontract_item_id === c.subcontract_item_id);
                const pend = clink.filter((l) => l.reconciliation_state === 'pending');
                return (
                  <div key={c.subcontract_item_id} className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={`cv-subimpact-candidate-${c.subcontract_item_id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm">
                        <span className="font-medium text-[#171717]">{c.supplier_name || 'Fornitore'}</span>
                        <span className="text-xs text-[#8a8a8a]"> · {c.subcontract_number || 'SUB'} · {c.subcontract_status}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-xs text-[#525252]">
                      <span>Corrente <b>{fmtQty(c.current_quantity, c.unit)}</b></span>
                      <span>Eseguita <b>{fmtQty(c.executed_quantity, c.unit)}</b></span>
                      <span>Modificabile <b>{fmtQty(c.modifiable_quantity, c.unit)}</b></span>
                      <span>Pending <b className="text-orange-600">{fmtQty(c.pending_quantity, c.unit)}</b></span>
                      <span>Riconciliata <b className="text-emerald-700">{fmtQty(c.reconciled_quantity, c.unit)}</b></span>
                      <span>Prezzo unitario sub <b>{formatCurrency(c.unit_price)}</b></span>
                    </div>

                    {/* Modifica in corso (pending) */}
                    {pend.map((l) => (
                      <div key={l.id} className="mt-2 rounded-md bg-orange-50 border border-orange-100 px-3 py-2 text-xs flex items-center justify-between gap-2 flex-wrap" data-testid={`cv-subimpact-pending-${l.id}`}>
                        <div className="flex items-center gap-2 text-orange-800">
                          <Link2 className="w-3.5 h-3.5" />
                          <span>Modifica sub in corso · {fmtQty(Math.abs(l.allocated_quantity_signed), t.unit)} · costo {formatCurrency(l.sub_delta_amount_snapshot)} · {l.modification_status}</span>
                        </div>
                        {canWrite && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busyLink === l.id} onClick={() => cancelLink(l.id)} data-testid={`cv-subimpact-cancel-${l.id}`}>Annulla</Button>
                            <Button size="sm" className="h-7 text-xs bg-[#FF7819] hover:bg-[#e56a10]" disabled={busyLink === l.id} onClick={() => applyLink(l.id)} data-testid={`cv-subimpact-apply-${l.id}`}>
                              {busyLink === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Rendi effettiva'}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* CTA genera modifica */}
                    {canWrite && c.is_reconcilable && (t.remaining_allocatable || 0) > 0 && (
                      <div className="mt-2">
                        <Button size="sm" className="h-8 text-xs bg-[#FF7819] hover:bg-[#e56a10]" onClick={() => openPreview(t, c)} data-testid={`cv-subimpact-generate-${c.subcontract_item_id}`}>
                          Genera modifica contrattuale <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Riepilogo economico variazione */}
              <div className="mt-3 rounded-lg bg-[#FAFAFA] border border-slate-100 p-3" data-testid="cv-subimpact-economic">
                <p className="text-[11px] uppercase tracking-wide text-[#9a9a9a] mb-1">Riepilogo economico variazione</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  <span className="text-[#737373]">Ricavo cliente <b className={t.economic.client_revenue_delta < 0 ? 'text-red-600' : 'text-emerald-700'}>{isZeroMoney(t.economic.client_revenue_delta) ? '' : (t.economic.client_revenue_delta < 0 ? '−' : '+')}{formatCurrency(Math.abs(t.economic.client_revenue_delta))}</b></span>
                  <span className="text-[#737373]">Costo sub effettivo <b className={t.economic.sub_cost_delta_effective < 0 ? 'text-red-600' : 'text-emerald-700'}>{isZeroMoney(t.economic.sub_cost_delta_effective) ? '' : (t.economic.sub_cost_delta_effective < 0 ? '−' : '+')}{formatCurrency(Math.abs(t.economic.sub_cost_delta_effective))}</b></span>
                  <span className="text-[#171717] font-semibold">Differenziale variazione <b>{isZeroMoney(t.economic.differential) ? '' : (t.economic.differential < 0 ? '−' : '+')}{formatCurrency(Math.abs(t.economic.differential))}</b></span>
                </div>
                {!isZeroMoney(t.economic.sub_cost_delta_pending) && (
                  <p className="text-[11px] text-orange-600 mt-1" data-testid="cv-subimpact-pending-cost">Costo sub in corso {t.economic.sub_cost_delta_pending < 0 ? '−' : '+'}{formatCurrency(Math.abs(t.economic.sub_cost_delta_pending))}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* PREVIEW MODAL */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="sm:max-w-[640px]" data-testid="cv-subimpact-preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima modifica contrattuale sub</DialogTitle>
            <DialogDescription>Genera una modifica in bozza (non effettiva) da confermare.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* CLIENTE */}
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-[#9a9a9a] mb-2">Cliente</p>
                  <p className="text-sm font-medium">{identityLabel(preview.target)}</p>
                  <p className="text-xs text-[#737373] mt-1">Delta cliente <b className="text-[#171717]">{preview.target.direction === 'DECREASE' ? '−' : '+'}{fmtQty(preview.target.client_delta_abs, preview.target.unit)}</b></p>
                  <p className="text-xs text-[#737373]">Valore cliente <b>{isZeroMoney(preview.target.client_delta_amount) ? '' : (preview.target.client_delta_amount < 0 ? '−' : '+')}{formatCurrency(Math.abs(preview.target.client_delta_amount))}</b></p>
                </div>
                {/* SUB */}
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-[#9a9a9a] mb-2">Subappalto</p>
                  <p className="text-sm font-medium">{preview.candidate.supplier_name || 'Fornitore'} · {preview.candidate.subcontract_number}</p>
                  {preview.data && (
                    <div className="text-xs text-[#737373] mt-1 space-y-0.5">
                      <p>Quantità corrente <b>{fmtQty(preview.data.subcontract.current_quantity, preview.target.unit)}</b></p>
                      <p>Eseguito <b>{fmtQty(preview.data.subcontract.executed_quantity, preview.target.unit)}</b></p>
                      <p>Massimo modificabile <b>{fmtQty(preview.data.subcontract.modifiable_quantity, preview.target.unit)}</b></p>
                      <p>Prezzo unitario sub <b>{formatCurrency(preview.data.subcontract.sub_unit_price)}</b></p>
                    </div>
                  )}
                </div>
              </div>

              {/* Delta modifica editabile */}
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <label className="text-xs text-[#737373]">Delta modifica ({preview.target.unit || 'q.tà'})</label>
                  <Input type="number" min="0" step="any" value={preview.mag}
                    className="h-9 w-40 mt-1" data-testid="cv-subimpact-delta-input"
                    onChange={(e) => setPreview((p) => ({ ...p, mag: e.target.value }))}
                    onBlur={() => { const m = Math.abs(Number(preview.mag) || 0); if (m === preview.dataMag) return; runPreview(preview.target, preview.candidate, preview.mag); }} />
                </div>
                {preview.data && (
                  <div className="text-sm">
                    <span className="text-[#737373]">Variazione costo sub </span>
                    <b className={preview.data.subcontract.sub_delta_amount < 0 ? 'text-red-600' : 'text-emerald-700'}>
                      {isZeroMoney(preview.data.subcontract.sub_delta_amount) ? '' : (preview.data.subcontract.sub_delta_amount < 0 ? '−' : '+')}{formatCurrency(Math.abs(preview.data.subcontract.sub_delta_amount))}
                    </b>
                  </div>
                )}
              </div>

              {preview.loading && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calcolo anteprima…</div>}
              {preview.error && (
                <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="cv-subimpact-preview-error">
                  {preview.error.code === 'ALREADY_EFFECTIVE' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
                  <span>
                    {preview.error.message}
                    {preview.error.max_reducible != null && preview.target.unit && (
                      <> Riduzione massima consentita: {fmtQty(preview.error.max_reducible, preview.target.unit)}.</>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="cv-subimpact-preview-cancel">Annulla</Button>
            <Button className="bg-[#FF7819] hover:bg-[#e56a10]" disabled={!preview?.data || preview?.submitting || preview?.loading}
              onClick={confirmDraft} data-testid="cv-subimpact-preview-confirm">
              {preview?.submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea modifica in bozza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcontractImpactSection;
